import axios from 'axios';
import { API } from './authApi';

/*
 * Owner tools (accounts listed in OWNER_EMAILS on the server): the email delivery test, the owner dashboard's stats and
 * recent deliveries, and the "tips & product updates" composer (opt-in readers only).
 */

/** Send one delivery test to the owner's own address → EmailTest { id, to, sender, reply_to, host, environment, transport, sent_at, status, tracking }. */
export const sendEmailTest = async () => (await axios.post(`${API}/admin/email-test`)).data;
/** Re-read the delivery event for one test (poll until status is 'delivered' / 'bounced' / 'complained' / 'failed'). */
export const getEmailTest = async (id) => (await axios.get(`${API}/admin/email-test/${encodeURIComponent(id)}`)).data;
/** The owner's recent tests, newest first. */
export const listEmailTests = async () => (await axios.get(`${API}/admin/email-test`)).data;

/** Dashboard numbers → { signups {total, today, last_7d, last_30d, by_day[]}, subscriptions {...}, tips_opt_in, emails_7d {...}, billing_live, email_transport }. */
export const getOwnerStats = async () => (await axios.get(`${API}/admin/stats`)).data;
/** Every email the app sent, newest first → [{ id, to, kind, subject, sent_at, status, transport, error }]. */
export const listRecentEmails = async (limit = 25) => (await axios.get(`${API}/admin/emails`, { params: { limit } })).data;

/** Product updates (tips & product updates): draft = { subject, body, cta_label?, cta_path? }. */
export const previewUpdate = async (draft) => (await axios.post(`${API}/admin/updates/preview`, draft)).data; // { html, subject, recipients, transport }
export const testUpdate = async (draft) => (await axios.post(`${API}/admin/updates/test`, draft)).data; // { ok, to, email_id }
export const sendUpdate = async (draft) => (await axios.post(`${API}/admin/updates/send`, draft)).data; // Campaign (202)
export const listUpdates = async () => (await axios.get(`${API}/admin/updates`)).data; // Campaign[]

/** Statuses after which polling stops. */
export const FINAL_EMAIL_TEST_STATUSES = ['delivered', 'bounced', 'complained', 'failed'];

export const EMAIL_TEST_STATUS = {
  accepted: { label: 'Accepted', detail: 'The provider has taken the message.', tone: 'wait' },
  sent: { label: 'Sent', detail: 'Handed to the recipient\u2019s mail server.', tone: 'wait' },
  delayed: { label: 'Delayed', detail: 'The recipient\u2019s server asked us to retry — it usually arrives within the hour.', tone: 'warn' },
  delivered: { label: 'Delivered', detail: 'The recipient\u2019s mail server accepted it — check the inbox (and spam).', tone: 'ok' },
  bounced: { label: 'Bounced', detail: 'The recipient\u2019s server refused it.', tone: 'bad' },
  complained: { label: 'Marked as spam', detail: 'The recipient reported the message as spam.', tone: 'bad' },
  failed: { label: 'Failed', detail: 'The provider could not send it.', tone: 'bad' },
};

/** What each `kind` in the email log means, for humans. */
export const EMAIL_KIND_LABEL = {
  welcome: 'Welcome',
  password_reset: 'Password reset',
  trial_started: 'Trial started',
  subscribed: 'Subscribed',
  trial_reminder: 'Trial ending soon',
  payment_failed: 'Payment failed',
  referral_reward: 'Referral reward',
  subscription_ended: 'Subscription ended',
  delivery_test: 'Delivery test',
  product_update: 'Product update',
  product_update_test: 'Product update (test)',
  owner_alert: 'Owner alert',
  billing: 'Billing',
  transactional: 'Account email',
};

/* ---- Conversion engine (owner ▸ Conversion engine page) ---- */
/** Everything about the Publisher conversion engine on this server → { engine, pdftoppm, last_error, events, counts_7d, selftest_available }. */
export const getEngineReport = async () => (await axios.get(`${API}/admin/engine`)).data;
/** Convert the bundled sample .pub right now → { ok, duration_ms, pages, bytes, message | code, engine }. Always 200. */
export const runEngineSelfTest = async () => (await axios.post(`${API}/admin/engine/selftest`)).data;
/** Throw the app's own runtime away and download it again (background) → { engine, started }. */
export const reinstallEngine = async () => (await axios.post(`${API}/admin/engine/reinstall`)).data;
/** One engine event in full — with LibreOffice's raw output (`log`) and run details (command, exit code, attempts). 404 once pruned (30 days). */
export const getEngineEvent = async (eventId) => (await axios.get(`${API}/admin/engine/events/${encodeURIComponent(eventId)}`)).data;

export const ENGINE_STATE = {
  system: { label: 'Ready · host LibreOffice', tone: 'ok' },
  ready: { label: 'Ready · app runtime', tone: 'ok' },
  installing: { label: 'Preparing…', tone: 'wait' },
  failed: { label: 'Install failed', tone: 'bad' },
  unavailable: { label: 'Unavailable', tone: 'warn' },
  unknown: { label: 'Checking…', tone: 'muted' },
};

export const ENGINE_EVENT_KIND = { import: 'Import', pdf: 'PDF', png: 'PNG', selftest: 'Self-test', install: 'Install', reinstall: 'Reinstall', invalidate: 'Runtime invalidated' };
export const ENGINE_EVENT_RESULT = {
  converted: { label: 'Converted', tone: 'ok' },
  rejected: { label: 'Rejected file', tone: 'muted' },
  failed: { label: 'Failed', tone: 'bad' },
  installed: { label: 'Installed', tone: 'ok' },
  requested: { label: 'Requested', tone: 'wait' },
  invalidated: { label: 'Reinstalling', tone: 'warn' },
  ok: { label: 'OK', tone: 'ok' },
};
